/* ------------------------------------------- */
/*   Save the main form in redux between steps as we change route
/* ------------------------------------------- */

import { createReducer } from 'AppSrc/core/reducers/create-reducer';
import {
  SAVE_FORM_DATA,
  SET_RISK_VALUE,
  SAVE_FORM_SECTION_ERRORS,
  CLEAR_FORM_ERRORS,
  DELETE_ALL_DATA_FOR_FORM,
  SUBMIT_FORM_REQUEST,
  SUBMIT_FORM_SUCCESS,
  SUBMIT_FORM_ERROR,
  CLEAR_SUBMIT_FORM_ERROR,
  SWITCH_LOCAL_WIIS_FOR_SERVER_WIIS,
  SET_SPONSOR_MAIN_FORM,
  SET_UNSAVED_RELATIONSHIPS_MAIN_FORM,
  SWITCH_LOCAL_RELATIONSHIPS_FOR_SERVER_RELATIONSHIPS,
} from 'AppSrc/core/actions/actions-main-form';
// It's easier to just have my own state that I can play around with and not break tests. Hope this is ok for now!
// import { summaryStateFreyjaEdition } from 'AppSrc/core/test/fixtures/redux/form-data/summary-state-freyja-edition';
import { CREATE_CRQ } from 'AppSrc/core/components/create-crq-screen/create-crq-screen-consts';
import { CREATE_UPDATE_CRQ_SUCCESS } from 'AppSrc/core/crq/crq-actions';
import {
  WORK_INFORMATION_ITEMS,
  APPROVERS_SECTION,
  RELATIONSHIPS,
  REFERENCES_SECTION,
} from 'AppSrc/core/components/forms/form-types/create-crq/form-definition/section-constants';
import { pou as pouField } from 'AppSrc/core/components/forms/form-types/create-crq/references-section/pou';
// rename following import to avoid clash with the section id constant
import { WORK_INFORMATION_ITEMS_FIELD_ID } from 'AppSrc/core/components/forms/form-types/create-crq/section-five/work-information-items/work-information-item-constants';
import { RAISING_CRQ } from 'AppSrc/core/components/forms/form-base-components/data-structure/constants';
import { APPROVER_GROUP_NAMES } from 'AppSrc/core/components/forms/form-types/create-crq/approvers-section/approvers-section-constants';
import { ADD_RELATIONSHIPS } from 'AppSrc/core/components/forms/form-types/create-crq/relationships/relationships-modal/relationship-constants';
import {
  SET_CREATE_CRQ_COORDINATOR_GROUP,
  SET_CREATE_CRQ_COORDINATOR,
  SET_CREATE_CRQ_COORDINATOR_AND_GROUP,
} from 'AppSrc/core/actions/actions-crq-coordinator';
import {
  SET_CREATE_CRQ_PEER_REVIEWER,
  SET_CREATE_CRQ_PEER_REVIEWER_GROUP,
} from 'AppSrc/core/actions/actions-crq-peer-reviewer';
import { ADD_SINGLE_APPROVER_GROUP_TO_CRQ_SUCCESS } from 'AppSrc/core/approvers/approver-groups-actions';
import { SET_POU_MAIN_FORM } from 'AppSrc/core/pou/pou-actions';

const mainFormFlow = RAISING_CRQ;

const mainFormDefaultState = {
  formData: {
    [CREATE_CRQ]: {
      formSectionsByDisplayOrder: [],
      dataByFormSectionId: {},
      riskValue: null,
      // errors across form sections to show on the summary page
      summaryErrors: {},
      coordinatorGroup: null,
      coordinatorId: null,
      coordinatorName: null,
      peerReviewerGroup: null,
      peerReviewerId: null,
      peerReviewerName: null,
      isSubmitting: false,
      submitError: null,
      crqId: null,
      // unsaved value for sponsor, that should be sent to the setSponsor endpoint
      // on next save
      unsavedSponsorValue: null,
      // section ids for unsaved relationships in the form data for mobile crqs
      unsavedRelationships: null,
      other_line_operator: null,
    },
  },
};



const saveFormData = (state, action) => {

  // FN: 2/1/2020
  // The order in which the form is rendered is dynamic (as it's easier to develop against this way
  // and there have also been discussions about the order changing depending on user roles).
  // As such we need to add each sectionId to formSectionsByDisplayOrder without duplications.
  const {
    formSectionsByDisplayOrder,
  } = state.formData[action.payload.formId];

  const newFormSectionsByDisplayOrder = [
    ...formSectionsByDisplayOrder,
  ];

  const fieldAlreadyInArray = formSectionsByDisplayOrder.includes(action.payload.sectionData.sectionId);

  if (!fieldAlreadyInArray) {
    newFormSectionsByDisplayOrder.push(action.payload.sectionData.sectionId);
  }

  return {
    ...state,
    formData: {
      ...state.formData,
      [action.payload.formId]: {
        ...state.formData[action.payload.formId],
        // Saves data
        dataByFormSectionId: {
          ...state.formData[action.payload.formId].dataByFormSectionId,
          [action.payload.sectionData.sectionId]: {
            ...action.payload.sectionData,
          },
        },
        // Saves order of form sections. Note: FN: 2/1/2020
        // Whilst the order of formSectionsByDisplayOrder is generated dynamically, the order currently
        // cannot be changed, ie
        // if formSectionsByDisplayOrder already includes 'CHANGE_QUESTIONS' from submitting data and
        // there's an undiscovered requirement that if a user clicks something on the 'CHANGE_TYPE' section
        // that means they no longer need to fill in 'CHANGE_QUESTIONS' or it should come later in the form,
        // we currently have no way of removing CHANGE_QUESTIONS or moving it's location. This will hopefully
        // never be needed though.
        formSectionsByDisplayOrder: [
          ...newFormSectionsByDisplayOrder,
        ],
      },
    },
  };
};

const setRiskValue = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [action.payload.formId]: {
      ...state.formData[action.payload.formId],
      riskValue: action.payload.riskValue,
    },
  },
});

// we keep existing state but use the default state for formId and thus deleting all of that data
const deleteAllDataForForm = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [action.payload.formId]: {
      ...mainFormDefaultState.formData[action.payload.formId],
    },
  },
});

const setSummaryErrorsForSection = (state, action) => {
  const { formId, sectionId, errors } = action.payload;
  return ({
    ...state,
    formData: {
      ...state.formData,
      [action.payload.formId]: {
        ...state.formData[formId],
        summaryErrors: {
          ...state.formData[formId].summaryErrors,
          [sectionId]: { ...errors },
        },
      },
    },
  });
};

const clearSummaryErrors = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [action.payload.formId]: {
      ...state.formData[action.payload.formId],
      summaryErrors: {},
    },
  },
});

const setCreateCrqCoordinatorGroup = (state, action) => {
  const coordinatorGroup = action.payload.coordinatorGroup;
  const currentGroup = state.formData[CREATE_CRQ].coordinatorGroup;
  // if the new coordinator group is the same as the existing group
  // we don't need to change state
  if (currentGroup === coordinatorGroup) {
    return state;
  }
  return ({
    ...state,
    formData: {
      ...state.formData,
      [CREATE_CRQ]: {
        ...state.formData[CREATE_CRQ],
        coordinatorGroup,
        // clear selected coordinator as the group has changed
        coordinatorId: null,
        coordinatorName: null,

      },
    },
  });
};

const setCreateCrqCoordinator = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [CREATE_CRQ]: {
      ...state.formData[CREATE_CRQ],
      coordinatorId: action.payload.coordinator ? action.payload.coordinator.id : null,
      coordinatorName: action.payload.coordinator ? action.payload.coordinator.full_name : null,
    },
  },
});

const setCreateCrqCoordinatorAndGroup = (state, action) => {
  const {
    coordinatorId,
    coordinatorName,
    coordinatorGroup,
  } = action.payload;
  return ({
    ...state,
    formData: {
      ...state.formData,
      [CREATE_CRQ]: {
        ...state.formData[CREATE_CRQ],
        coordinatorId,
        coordinatorName,
        coordinatorGroup,
      },
    },
  });
};

const setCreateCrqPeerReviewerGroup = (state, action) => {
  const peerReviewerGroup = action.payload.peerReviewerGroup;
  const currentGroup = state.formData[CREATE_CRQ].peerReviewerGroup;
  // if the new coordinator group is the same as the existing group
  // we don't need to change state
  if (currentGroup === peerReviewerGroup) {
    return state;
  }
  return ({
    ...state,
    formData: {
      ...state.formData,
      [CREATE_CRQ]: {
        ...state.formData[CREATE_CRQ],
        peerReviewerGroup,
        // clear selected coordinator as the group has changed
        peerReviewerId: null,
        peerReviewerName: null,

      },
    },
  });
};

const setCreateCrqPeerReviewer = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [CREATE_CRQ]: {
      ...state.formData[CREATE_CRQ],
      peerReviewerId: action.payload.peerReviewer ? action.payload.peerReviewer.id : null,
      peerReviewerName: action.payload.peerReviewer ? action.payload.peerReviewer.full_name : null,
    },
  },
});

const setSponsor = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [action.payload.formId]: {
      ...state.formData[action.payload.formId],
      unsavedSponsorValue: action.payload.sponsor,
    },
  },
});

const setUnsavedRelationships = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [action.payload.formId]: {
      ...state.formData[action.payload.formId],
      unsavedRelationships: action.payload.unsavedRelationships,
    },
  },
});

const sumbitStarted = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [action.payload.formId]: {
      ...state.formData[action.payload.formId],
      isSubmitting: true,
      submitError: null,
    },
  },
});

const submitSuccess = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [action.payload.formId]: {
      ...state.formData[action.payload.formId],
      isSubmitting: false,
      // also means we've updated the sponsor to the database
      // so we can set sponsor back to null
      unsavedSponsorValue: null,
    },
  },
});

const submitError = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [action.payload.formId]: {
      ...state.formData[action.payload.formId],
      isSubmitting: false,
      submitError: action.payload.error,
    },
  },
});

const clearSubmitError = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [action.payload.formId]: {
      ...state.formData[action.payload.formId],
      submitError: null,
    },
  },
});

const createUpdateCrqSuccess = (state, action) => ({
  ...state,
  formData: {
    ...state.formData,
    [CREATE_CRQ]: {
      ...state.formData[CREATE_CRQ],
      crqId: action.payload.crqId,
    },
  },
});


const switchLocalWIIsForServerWIIs = (state, action) => {
  const {
    formId,
    wiis,
  } = action.payload;
  const currentForm = state.formData[formId];

  const wIISectionExists = currentForm && currentForm.dataByFormSectionId[WORK_INFORMATION_ITEMS] ? true : false;
  const wIISection = wIISectionExists ? currentForm.dataByFormSectionId[WORK_INFORMATION_ITEMS] : {};
  const dataByFieldId = wIISection.dataByFieldId ? wIISection.dataByFieldId : {};
  const wiiFieldData = dataByFieldId[WORK_INFORMATION_ITEMS_FIELD_ID] ? dataByFieldId[WORK_INFORMATION_ITEMS_FIELD_ID] : {};


  return ({
    ...state,
    formData: {
      ...state.formData,
      [formId]: {
        ...currentForm,
        dataByFormSectionId: {
          ...currentForm.dataByFormSectionId,
          [WORK_INFORMATION_ITEMS]: {
            ...wIISection,
            dataByFieldId: {
              ...dataByFieldId,
              [WORK_INFORMATION_ITEMS_FIELD_ID]: {
                ...wiiFieldData,
                value: wiis,
              },
            },
          },
        },
      },
    },
  });
};


// action handler to set a specific value for an approver group in the approvers section
// to be readonly. This is the result of successfully submitting it to the add_approver_group endpoint
const setApproverReadOnly = (state, action) => {
  const {
    id,
    formFlow,
  } = action.payload;
  // We only need to update the redux data if the formFlow is RAISING_CRQ (mainFormFlow).
  if (formFlow !== mainFormFlow) {
    return state;
  }

  const currentFormData = state.formData[CREATE_CRQ];
  if (!currentFormData) {
    return state;
  }

  const currentSection = currentFormData && currentFormData.dataByFormSectionId[APPROVERS_SECTION];
  // if we havent visited the approvers section yet, do nothing
  if (!currentSection) {
    return state;
  }
  const currentValue = currentSection.dataByFieldId[APPROVER_GROUP_NAMES].value;
  // make a new array, where the entry for the approver with the relevant has readonly = true
  const newValue = currentValue.map((approver) => {
    const idMatches = approver.id === id;
    return idMatches ? { ...approver, readOnly: true } : approver;
  });
  return ({
    ...state,
    formData: {
      ...state.formData,
      [CREATE_CRQ]: {
        ...currentFormData,
        dataByFormSectionId: {
          ...currentFormData.dataByFormSectionId,
          [APPROVERS_SECTION]: {
            ...currentSection,
            dataByFieldId: {
              ...currentSection.dataByFieldId,
              [APPROVER_GROUP_NAMES]: {
                ...currentSection.dataByFieldId[APPROVER_GROUP_NAMES],
                value: newValue,
              },
            },
          },
        },
      },
    },
  });
};

const switchLocalRelationshipsForServerRelationships = (state, action) => {
  const {
    formId,
    relationships,
  } = action.payload;
  const currentForm = state.formData[formId];

  const relationshipsSectionExists = currentForm && currentForm.dataByFormSectionId[RELATIONSHIPS] ? true : false;
  const relationshipsSection = relationshipsSectionExists ? currentForm.dataByFormSectionId[RELATIONSHIPS] : {};
  const dataByFieldId = relationshipsSection.dataByFieldId ? relationshipsSection.dataByFieldId : {};
  const wiiFieldData = dataByFieldId[ADD_RELATIONSHIPS] ? dataByFieldId[ADD_RELATIONSHIPS] : {};


  return ({
    ...state,
    formData: {
      ...state.formData,
      [formId]: {
        ...currentForm,
        dataByFormSectionId: {
          ...currentForm.dataByFormSectionId,
          [RELATIONSHIPS]: {
            ...relationshipsSection,
            dataByFieldId: {
              ...dataByFieldId,
              [ADD_RELATIONSHIPS]: {
                ...wiiFieldData,
                value: relationships,
              },
            },
          },
        },
      },
    },
  });
};

const setPou = (state, action) => {
  const {
    formId,
    pou,
  } = action.payload;
  const currentFormData = state.formData[formId];
  if (!currentFormData) {
    return state;
  }
  const existingSection = currentFormData && currentFormData.dataByFormSectionId[REFERENCES_SECTION];
  // the section data may not exist if this is the first time through the form
  // but we want to add the POU value to it, so we default to an empty object
  const section = existingSection ? existingSection : {};
  const pouData = section && section.dataByFieldId ? section.dataByFieldId[pouField.id] : {};
  return ({
    ...state,
    formData: {
      ...state.formData,
      [formId]: {
        ...currentFormData,
        dataByFormSectionId: {
          ...currentFormData.dataByFormSectionId,
          [REFERENCES_SECTION]: {
            ...section,
            dataByFieldId: {
              ...section.dataByFieldId,
              [pouField.id]: {
                ...pouData,
                value: pou,
                displayValues: pou,
              },
            },
          },
        },
      },
    },
  });
};


const actionHandlers = {
  [SAVE_FORM_DATA]: saveFormData,
  [SET_RISK_VALUE]: setRiskValue,
  [DELETE_ALL_DATA_FOR_FORM]: deleteAllDataForForm,
  [SAVE_FORM_SECTION_ERRORS]: setSummaryErrorsForSection,
  [CLEAR_FORM_ERRORS]: clearSummaryErrors,
  [SET_CREATE_CRQ_COORDINATOR_GROUP]: setCreateCrqCoordinatorGroup,
  [SET_CREATE_CRQ_COORDINATOR]: setCreateCrqCoordinator,
  [SET_CREATE_CRQ_COORDINATOR_AND_GROUP]: setCreateCrqCoordinatorAndGroup,
  [SET_CREATE_CRQ_PEER_REVIEWER]: setCreateCrqPeerReviewer,
  [SET_CREATE_CRQ_PEER_REVIEWER_GROUP]: setCreateCrqPeerReviewerGroup,
  [SUBMIT_FORM_REQUEST]: sumbitStarted,
  [SUBMIT_FORM_SUCCESS]: submitSuccess,
  [SUBMIT_FORM_ERROR]: submitError,
  [CLEAR_SUBMIT_FORM_ERROR]: clearSubmitError,
  [CREATE_UPDATE_CRQ_SUCCESS]: createUpdateCrqSuccess,
  [SWITCH_LOCAL_WIIS_FOR_SERVER_WIIS]: switchLocalWIIsForServerWIIs,
  [SWITCH_LOCAL_RELATIONSHIPS_FOR_SERVER_RELATIONSHIPS]: switchLocalRelationshipsForServerRelationships,
  [SET_SPONSOR_MAIN_FORM]: setSponsor,
  [SET_UNSAVED_RELATIONSHIPS_MAIN_FORM]: setUnsavedRelationships,
  [ADD_SINGLE_APPROVER_GROUP_TO_CRQ_SUCCESS]: setApproverReadOnly,
  [SET_POU_MAIN_FORM]: setPou,
};

const mainFormReducer = createReducer(mainFormDefaultState, actionHandlers);


export {
  mainFormDefaultState,
  mainFormReducer,
};





